"use client";

import React, { useRef, useState } from "react";
import TextField from "@/components/textField";
import PersonIcon from "@mui/icons-material/Person";
import MailIcon from "@mui/icons-material/Mail";
import MessageIcon from "@mui/icons-material/Message";
import ThemeUIProvider from "@/components/themeUIProvider";
import * as yup from "yup";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import contact_insert from "@/data/contact_insert";
import Button from "@/components/Button";
import MobilePhoneSelect from "@/components/fields/MobilePhoneSelect";
import Captcha from "@/components/fields/Captcha";

const schema = yup.object().shape({
  name: yup.string().required("Required field"),
  mobileCountryCode: yup
    .string()
    .required("Country code is Required field")
    .trim(),
  mobileNumber: yup
    .string()
    .min(6, "Mobile should be at least 6 digits")
    .required("Mobile number is Required field")
    .trim(),
  email: yup.string().email("Invalid email").required("Required field"),
});

const defaultValues = {
  type: "in-person",
  name: "",
  mobileCountryCode: "971",
  mobileNumber: "",
  email: "",
  message: "",
};

const ScheduleView = ({ className, title, url, submitText = "Submit" }) => {
  const formRef = useRef(null);
  const captchaRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState(null);
  const [values, setValues] = useState(defaultValues);
  const mobilePhoneSelectRef = useRef(null);

  const isValidForm = Object.keys(errors ?? {})?.length === 0;

  async function onSubmit(event) {
    event.preventDefault();
    setIsLoading(true);
    setErrors(null);
    try {
      const isValid = await schema.validate(values, { abortEarly: false });

      const isValidCaptcha = captchaRef.current?.getResponse();

      if (!isValidCaptcha) {
        setSuccess(false);
        setErrors((errors) => ({ ...errors, root: "Captcha is required" }));
        return;
      }

      setErrors((errors) => ({ ...errors, root: "" }));

      if (isValid) {
        const text =
          title &&
          url &&
          `Interested in "${title}" - [${url}] and want to schedule a view (${values?.type === "in-person" ? "in person" : "in a call"}). \r\n${values?.message}`;

        await contact_insert({
          ...values,
          remarks: text,
        });

        setErrors(null);
        setSuccess(true);
        setValues(defaultValues);

        mobilePhoneSelectRef?.current?.reset();
        captchaRef.current?.reset();
        formRef.current.reset();
      }
    } catch (error) {
      const formErrors = { root: "" };
      error.inner?.forEach((err) => {
        formErrors[err.path] = err.errors.join(", ");
      });

      if (!error.inner) {
        formErrors.root = error.message;
      }

      setErrors(formErrors);
    } finally {
      setIsLoading(false);
    }
  }

  const handleFieldChange = (field) => {
    setValues({ ...values, ...field });
  };

  return (
    <ThemeUIProvider>
      <form
        onSubmit={onSubmit}
        ref={formRef}
        className={`flex flex-col gap-4 md:gap-6 max-w-full ${className}`}
      >
        {success && isValidForm && (
          <div className="text-green-600 font-bold">
            Thank you! <br />
            We will be in touch with you shortly.
          </div>
        )}

        {errors?.root && (
          <div className="text-red-600 font-bold">{errors.root}</div>
        )}

        <ToggleButtonGroup
          color="primary"
          value={values.type}
          exclusive
          onChange={(e) => {
            handleFieldChange({ type: e.target.value });
          }}
          aria-label="type"
        >
          <ToggleButton value="in-person" className="grow">
            In Person
          </ToggleButton>
          <ToggleButton value="video-call" className="grow">
            Video Call
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          label="Name"
          name="name"
          iconEnd={<PersonIcon />}
          value={values.name}
          onChange={(e) => {
            handleFieldChange({ name: e.target.value });
          }}
          error={errors?.name ? true : null}
          helperText={errors?.name}
        />

        <MobilePhoneSelect
          onChange={(values) => handleFieldChange(values)}
          ref={mobilePhoneSelectRef}
          errors={errors}
        />

        <TextField
          label="Email"
          name="email"
          value={values.email}
          iconEnd={<MailIcon />}
          onChange={(e) => {
            handleFieldChange({ email: e.target.value });
          }}
          error={errors?.email ? true : null}
          helperText={errors?.email}
        />
        <TextField
          label="Message"
          name="message"
          value={values.message}
          multiline
          rows={4}
          iconEnd={<MessageIcon />}
          onChange={(e) => {
            handleFieldChange({ message: e.target.value });
          }}
        />
        <div className="flex-1 max-w-full">
          <Captcha ref={captchaRef} disabled={isLoading} />
        </div>
        <Button
          variant="contained"
          size="large"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? "Submitting" : submitText}
        </Button>
      </form>
    </ThemeUIProvider>
  );
};

export default ScheduleView;
